import axios, { AxiosError } from "axios"
import type { ErrorResponse } from "react-router-dom"
import {
  useMutation,
  useQuery,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query"
import { customAxios } from "@/shared/lib/customAxios"
import type { ListRequest, ListResponse } from "@/shared/types/commonResponse"
import { getMediaType } from "@/shared/utils/media"

// POST /star/feed/list  ------------------------------ Feed List

export interface FeedContent {
  id: number
  content: string
  mediaUrl: string
  regDt: string
  signedUrl?: string
}

export type FeedListResponse = ListResponse<FeedContent>

const FeedList = ({
  Language,
  requestDto,
  angkorId,
}: ListRequest): Promise<FeedListResponse> => {
  return customAxios
    .post<FeedListResponse>("star/feed/list", requestDto, {
      headers: {
        Language: Language,
        angkorId: angkorId,
      },
    })
    .then((res) => res.data)
}

export const useFeedListWithSignedUrlQuery = (
  request: ListRequest,
  options?: { enabled?: boolean }
): UseQueryResult<FeedListResponse, Error> => {
  return useQuery<FeedListResponse, Error>({
    queryKey: ["feedListWithSigned", request],
    queryFn: () => fetchFeedListWithSignedUrl(request),
    gcTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    enabled: options?.enabled,
  })
}

// POST /star/feed/list  ------------------------------ Feed List

// POST /v1/signed/geturl{FileName} -------------------
// 이미지 파일명을 기반으로 Signed URL 발급 요청

export interface FormatImageRequest {
  FileName: string
  mediaType: string
}

interface FormatImageResponse {
  authKey: string
  fileSize: number
  signedUrl: string
  tokenType: string
}

export const getFormattedImage = async (
  request: FormatImageRequest
): Promise<FormatImageResponse> => {
  const res = await axios.post<FormatImageResponse>(
    `${import.meta.env.VITE_API_URL_MEDIA}v1/signed/geturl/${request.FileName}`,
    {
      angkorId: "cms",
      division: "post",
      fileType: request.mediaType,
      grantType: "session",
    },
    {
      headers: {
        AppKey: import.meta.env.VITE_S3_APP_KEY,
        AuthKey: `bearer ${import.meta.env.VITE_S3_SECRET_KEY}`,
      },
    }
  )
  return res.data
}

export const useFormatImageMutation = (): UseMutationResult<
  FormatImageResponse,
  AxiosError<ErrorResponse>,
  FormatImageRequest,
  unknown
> => {
  return useMutation<
    FormatImageResponse,
    AxiosError<ErrorResponse>,
    FormatImageRequest,
    unknown
  >({
    mutationFn: getFormattedImage,
  })
}

// ------------------------------ POST /v1/signed/geturl{FileName} ------------------------------

async function fetchFeedListWithSignedUrl(
  request: ListRequest
): Promise<FeedListResponse> {
  // 1. 원본 피드 리스트 요청
  const feedListData = await FeedList(request)

  // 2. signedUrl 모두 요청
  const formatImageRequests = feedListData.content.map((item) => ({
    FileName: item.mediaUrl,
    mediaType: getMediaType(item.mediaUrl),
  }))

  const signedUrlResults = await Promise.all(
    formatImageRequests.map((req) => getFormattedImage(req))
  )

  // 3. signedUrl 붙여서 반환
  const feedListWithSigned = feedListData.content.map((item, idx) => ({
    ...item,
    signedUrl: signedUrlResults[idx]?.signedUrl,
  }))

  return { ...feedListData, content: feedListWithSigned }
}
